// 氏名の復唱候補を組み立てる (spec 2026-08-20 §3.4、段 3)。純粋関数のみ (I/O なし)。
//
// 供給元は 3 つある。順位付け (`ranked`、第 2 ASR を含む 2 つの書き起こしを LLM に
// 並べさせた出力) → 現行の LLM 抽出 (`display`) → Scribe の生テキスト (`asrDisplay`)。
// **順位付けは並びの先頭に入るだけで、後ろの供給元を押し出さない。**
//
// **`ranked` が空なら出力は現行の `cands` と 1 ビットも同じ** — 第 2 ASR も順位付けも
// 落ちたら現行の挙動に倒れる (原則 5)。これがこのモジュールを純関数として切り出した
// 理由で、テスト 1 本 (§8.1-1) で固定できる。
//
// **denied のフィルタを引数で受けるのが D18 の実装である。** 呼び出し側で filter して
// から slice する形にすると「どちらを先にやるか」がコンポーネント側に残る。順序を
// ここに閉じ込めると、変異 (「denied より前で切る」) が単体テストで殺せる。

import { formatName } from "./nameNormalize";
import { matchKey } from "./kanaKey";

export type CandidateSource = "ranked" | "llm" | "asr";

// key は matchKey の出力で、**突き合わせ専用** (画面にも TTS にも出さない)。
export type NameCandidate = { display: string; key: string; source: CandidateSource };

// **1 回の認識サイクルで本人に復唱する候補の数**の上限。候補列の長さの上限ではない。
// 実測したのが「2 位以内」であり、復唱 1 往復の代償 (読み上げ 2.6 秒 + 応答) が
// 線形に増えるため 2 に置く (D8)。`ranked` が空のとき供給元は高々 2 件なので、
// この上限は現行の経路では一度も働かない。
export const MAX_CANDIDATES = 2;

export function buildNameCandidates(input: {
  // 順位付けの出力 (生の綴り、高々 3 件)。空 = 順位付けが無い = 現行と同一。
  // 3 件目は提示しないが、次サイクルの未提示の予備として効く (D19)。
  ranked: string[];
  // extractName 経由 (現行の llm)。resolveName が formatName を通してある。
  display: string;
  // Scribe の生テキスト経由 (現行の asr)。同じく formatName 済み。
  asrDisplay: string;
  // deniedRef.current — 本人が「いいえ」と答えた鍵。**切り落としより前に効かせる** (D18)。
  denied: ReadonlySet<string>;
}): {
  // 供給元をすべて並べたもの。**切っていない** (最大 5 件)。
  // findSpellingPair は否定済みの表記も含めて突き合わせる必要があるので、
  // ここを切ると「音では決まらない差」の 2 択 (D25-4) が出なくなる。
  all: NameCandidate[];
  // all から denied を落とし、**そのあと** MAX_CANDIDATES で切ったもの。
  fresh: NameCandidate[];
} {
  const all: NameCandidate[] = [];

  const push = (source: CandidateSource, text: string): void => {
    if (!text) return;
    const key = matchKey(text);
    // 鍵が同じなら先に来たほうを残す (現行の cands と同じ規則。並び順が優先順)。
    // **残すのは生の綴り** — canon 形に寄せると画面と TTS に出せなくなる。
    if (all.some((c) => c.key === key)) return;
    all.push({ display: text, key, source });
  };

  for (const r of input.ranked) {
    // 順位付けの出力は LLM の JSON そのままなので、要素が文字列でない回がありうる。
    // 型 (string[]) では防げないため実行時に落とす。
    if (typeof r !== "string") continue;
    // ひらがな → カタカナ / 漢字除去 / 語尾除去 / 空白除去 (D12) を当てる。
    // display / asrDisplay は resolveName が既に通しているのでここでは当てない。
    push("ranked", formatName(r, ""));
  }
  push("llm", input.display);
  push("asr", input.asrDisplay);

  // **この 2 行の順序が仕様である (D18)。** 先に切ると、順位付けが 2 件返るたびに
  // 現行の候補 (display / asrDisplay) が押し出され、denied が 2 鍵入った次の
  // サイクルで fresh が空になって no-progress のキーボード退避に落ちる。
  const fresh = all.filter((c) => !input.denied.has(c.key)).slice(0, MAX_CANDIDATES);

  return { all, fresh };
}
